import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@workspace/app-components';
import { type ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { DocumentContent } from './presentation';
import { loadGameRules, type RulesModule } from './rules-catalog';

export function GameRulesReader({ gameId }: { gameId: string }) {
  const [content, setContent] = useState<RulesModule | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const article = useRef<HTMLElement>(null);
  const documentBody = useRef<HTMLDivElement>(null);
  const mobileOutline = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    let active = true;
    setContent(null);
    setFailure(null);
    void (async () => {
      const loaded = await loadGameRules(gameId);
      if (active) setContent(loaded);
    })().catch((cause: unknown) => {
      console.error(`[Game rules ${gameId}] Failed to load:`, cause);
      if (active) setFailure(cause instanceof Error ? cause.message : String(cause));
    });
    return () => {
      active = false;
    };
  }, [gameId, revision]);

  useEffect(() => {
    const viewport = article.current;
    const body = documentBody.current;
    if (!content || failure || !viewport || !body) return;

    const updateSection = () => {
      const headings = content.headings
        .filter(({ depth }) => depth === 2)
        .map(({ id }) => body.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`))
        .filter((heading) => heading !== null);
      let current: HTMLElement | undefined = headings[0];
      const top = viewport.getBoundingClientRect().top;
      for (const heading of headings) {
        const margin = Number.parseFloat(getComputedStyle(heading).scrollMarginTop);
        if (heading.getBoundingClientRect().top > top + margin + 1) break;
        current = heading;
      }
      if (viewport.scrollTop > 0 && viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <= 1) {
        current = headings.at(-1);
      }
      setActiveSection(current?.id ?? null);
    };

    updateSection();
    viewport.addEventListener('scroll', updateSection, { passive: true });
    const resize = new ResizeObserver(updateSection);
    resize.observe(viewport);
    resize.observe(body);
    return () => {
      viewport.removeEventListener('scroll', updateSection);
      resize.disconnect();
    };
  }, [content, failure]);

  const goTo = useCallback((id: string) => {
    const heading = article.current?.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`);
    if (!heading) {
      console.error(`[Game rules] Missing section: ${id}`);
      setFailure(`The linked rules section "${id}" could not be found.`);
      return;
    }
    if (mobileOutline.current) mobileOutline.current.open = false;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
    heading.scrollIntoView({ block: 'start' });
  }, []);
  const RulesLink = useCallback(
    ({ href, children, ...props }: ComponentProps<'a'>) => (
      <a
        {...props}
        href={href}
        onClick={(event) => {
          if (href?.startsWith('#')) {
            event.preventDefault();
            goTo(href.slice(1));
          }
        }}
      >
        {children}
      </a>
    ),
    [goTo],
  );

  if (failure) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Rules unavailable</AlertTitle>
        <AlertDescription>
          <p>{failure}</p>
          <Button variant="outline" onClick={() => setRevision((value) => value + 1)}>
            Retry rules
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  if (!content)
    return (
      <p role="status" className="p-4 text-muted-foreground">
        Loading rules...
      </p>
    );

  const sectionLinks = content.headings
    .filter(({ depth }) => depth === 2)
    .map(({ id, text }) => (
      <Button
        key={id}
        variant={activeSection === id ? 'secondary' : 'ghost'}
        aria-current={activeSection === id ? 'location' : undefined}
        className="min-h-11 justify-start whitespace-normal text-left"
        onClick={() => goTo(id)}
      >
        {text}
      </Button>
    ));

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 md:flex-row">
      <details ref={mobileOutline} className="shrink-0 rounded-lg border px-3 md:hidden">
        <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium">On this page</summary>
        <nav aria-label="Rules sections" className="flex max-h-[30dvh] flex-col overflow-y-auto pb-2">
          {sectionLinks}
        </nav>
      </details>
      <nav
        aria-label="Rules sections"
        className="hidden w-44 shrink-0 flex-col gap-1 overflow-y-auto border-r pr-3 md:flex"
      >
        {sectionLinks}
      </nav>
      <article
        ref={article}
        aria-label="Game rules"
        className="document-prose min-h-0 min-w-0 flex-1 overflow-y-auto px-1 pb-6 md:px-4"
      >
        <div ref={documentBody} className="flow-root">
          <DocumentContent key={gameId} Content={content.default} components={{ a: RulesLink }} />
        </div>
      </article>
    </div>
  );
}

export function GameRulesDialog({
  gameId,
  displayName,
  open,
  onOpenChange,
  returnFocus,
}: {
  gameId: string;
  displayName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocus?: HTMLElement | null;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[90dvh] w-[calc(100%-2rem)] min-w-0 flex-col sm:max-w-5xl"
        finalFocus={returnFocus ? () => returnFocus : undefined}
        portalContainer={returnFocus?.closest<HTMLElement>('[data-fullscreen-target]') ?? undefined}
      >
        <DialogHeader className="shrink-0 pr-8">
          <DialogTitle>{displayName}</DialogTitle>
        </DialogHeader>
        {open && <GameRulesReader key={gameId} gameId={gameId} />}
      </DialogContent>
    </Dialog>
  );
}
